'use client';
import { Button, Checkbox, GlobalLoading, Input, InputNumber, Modal } from '@/shared/components';
import React, { useEffect, useRef, useState } from 'react';
import { useGetProductByCatalogId } from '@/hooks/cart.hook';
import { number } from 'mathjs';
import { NAMESPACE, useLang } from '@/hooks/use-lang';
import { useProductStore } from '../product-store';
import { useCartStore } from '../cart-store';
import { usePriceStore } from '@/store/price-store';
import { processNumberString } from '@/app/utils';
import { QUICK_ADD_ERROR_MESSAGE_MAP } from '../constants';
import { useEventTracking } from '@/shared/tracking/use-tracking';
import { getProductPrice } from '@/shared/tracking/utils';
import { CartQtpModel } from './cart-qtp-model';

interface IProps {
  updateQuantity?: any;
}

export const ShoppingSummary = ({ updateQuantity }: IProps) => {
  const { t } = useLang(NAMESPACE.cart);
  const cartStore = useCartStore();
  const productStore = useProductStore();
  const { productPrice, promotionInfo } = usePriceStore();
  const [quantity, setQuantity] = useState<number>(1);
  const [catalogNumber, setCatalogNumber] = useState<string>('');
  const getProductByCatalogId = useGetProductByCatalogId();
  const [open, setOpen] = useState(false);
  const [removeAllOpen, setRemoveAllOpen] = useState(false);
  const catalogNumberRef = useRef<string>();
  const [errorText, setErrorText] = useState('');
  const [emptyButtonDisabled, setEmptyButtonDisabled] = useState(false);
  const [selectButtonDisabled, setSelectButtonDisabled] = useState(false);
  const [addButtonDisabled, setAddButtonDisabled] = useState(false);
  const [isIndeterminate, setIsIndeterminate] = useState(false);
  const [checked, setChecked] = useState(false);
  const [qtpConfirmModalInfo, setQtpConfirmModalInfo] = useState<{
    type: 'removeItem' | 'removeAll';
    status: 'mandatory' | 'optional';
  }>();

  const { trackEvent } = useEventTracking();
  useEffect(() => {
    setChecked(cartStore.selectedCatalogNumbers.length > 0);
  }, [cartStore.selectedCatalogNumbers]);

  useEffect(() => {
    setIsIndeterminate(cartStore.selectedCatalogNumbers.length !== productStore.products.length);
  }, [cartStore.selectedCatalogNumbers, productStore.products]);

  useEffect(() => {
    setAddButtonDisabled(!quantity || !catalogNumber?.trim() || number(quantity) === 0);
  }, [quantity, catalogNumber]);

  useEffect(() => {
    setEmptyButtonDisabled(productStore.products.length <= 0);
  }, [productStore.products]);

  useEffect(() => {
    setSelectButtonDisabled(cartStore.selectedCatalogNumbers.length <= 0);
  }, [cartStore.selectedCatalogNumbers]);

  const onQuantityChange = (value: string) => {
    setQuantity(processNumberString(value));
  };
  const onAdd = () => {
    if (!catalogNumber || !quantity) return;
    if (quantity === 0) return;
    GlobalLoading.show();
    getProductByCatalogId
      .mutateAsync(catalogNumber.trim().toUpperCase())
      .then((response) => {
        if (response.code === '200') {
          const product = response.data[0];
          if (product.productStatus !== 'DISCONTINUED' && product.entitled !== false) {
            if (promotionInfo?.promoType === 'FBP') {
              updateQuantity();
            }
            const promoMessage = promotionInfo?.promotionMessages.find(
              (item) => item.catalogNumber === product.catalogNumber
            );
            if (promoMessage) {
              productStore.addProduct(
                {
                  ...product,
                  quantity,
                },
                promoMessage?.promotionQuantity
              );
            } else {
              productStore.addProduct({
                ...product,
                quantity,
              });
            }

            setCatalogNumber('');
            setQuantity(1);
            setErrorText('');
            trackEvent({
              key: 'cart',
              action: 'addToCart',
              payload: {
                products: [
                  {
                    name: product?.productTitle,
                    catalogId: product.catalogNumber,
                    quantity: quantity,
                    uom: product.unitSize,
                    ...(productPrice[product.catalogNumber] && {
                      price: getProductPrice(productPrice[product.catalogNumber]) * quantity,
                    }),
                    placement: 'shopping cart',
                    method: 'forgotsomething',
                  },
                ],
              },
            });
          } else {
            setErrorText('sku.discontinued');
          }
        } else {
          catalogNumberRef.current = catalogNumber;
          setErrorText(QUICK_ADD_ERROR_MESSAGE_MAP[response.code] ?? 'sku.not.available');
        }
      })
      .finally(() => {
        GlobalLoading.hide();
      });
  };

  const onSelectAll = (checked: boolean) => {
    if (checked) {
      const selectedCatalogNumbers = productStore.products
        .filter((e) => {
          const price = productPrice[e.catalogNumber];
          return e.productStatus !== 'BLOCKED' && price.priceStatus === 'Orderable';
        })
        .map((e) => e.catalogNumber);
      cartStore.changeSelectedProducts(selectedCatalogNumbers);
    } else {
      cartStore.changeSelectedProducts([]);
    }
  };

  const onRemovePartial = () => {
    if (cartStore.selectedCatalogNumbers.length > 0) {
      setOpen(true);
    }
  };

  const onRemoveProducts = async () => {
    GlobalLoading.show();
    const selectedProducts = productStore.products.filter((e) =>
      cartStore.selectedCatalogNumbers.includes(e.catalogNumber)
    );
    trackEvent({
      key: 'cart',
      action: 'scRemove',
      payload: {
        products: selectedProducts.map((product) => {
          return {
            name: product.productTitle,
            catalogId: product.catalogNumber,
          };
        }),
      },
    });
    await productStore.removeProduct(cartStore.selectedCatalogNumbers);
    setOpen(false);
    GlobalLoading.hide();
  };

  const onRemoveAll = () => {
    GlobalLoading.show();
    trackEvent({
      key: 'cart',
      action: 'scRemove',
      payload: {
        products: productStore.products.map((product) => {
          return {
            name: product.productTitle,
            catalogId: product.catalogNumber,
          };
        }),
      },
    });
    productStore.updateProducts([]);
    cartStore.changeSelectedProducts([]);
    setRemoveAllOpen(false);
    GlobalLoading.hide();
  };

  const onCatalogChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (errorText) {
      setErrorText('');
      catalogNumberRef.current = '';
    }
    setCatalogNumber(e.target.value);
  };

  return (
    <div
      className={`grid 2xl:grid-cols-[350px_1fr] grid-cols-1 pt-[26px] items-center print:hidden border-0 border-t border-solid border-gray-300 ${
        errorText ? 'mb-14' : 'mb-10'
      }`}
    >
      <div className="flex items-center sm:flex-col sm:items-start">
        <Checkbox
          label={<span className="text-info">{t('all')}</span>}
          className="flex-shrink-0 mb-0"
          indeterminate={isIndeterminate}
          checked={checked}
          onChange={onSelectAll}
        />
        <div className="sm:flex sm:flex-col sm:gap-y-2 sm:items-start sm:mt-4">
          <Button
            type="link"
            className="p-0 h-auto leading-none ml-8 sm:ml-0 text-sm font-normal disabled:cursor-not-allowed disabled:bg-transparent disabled:border-none disabled:text-[#A5A5AE] flex-shrink-0"
            onClick={() => {
              const qtpSKUMap = usePriceStore.getState()?.promotionInfo?.qtpSKUMap;
              const selectedProducts = productStore.products.filter((e) =>
                cartStore.selectedCatalogNumbers.includes(e.catalogNumber)
              );

              let hasQtpItem = false;
              let hasMandatoryQtp = false;

              for (const product of selectedProducts) {
                const currentItem = qtpSKUMap?.[product.catalogNumber];
                if (currentItem) {
                  hasQtpItem = true;
                  if (currentItem.required) {
                    hasMandatoryQtp = true;
                    break;
                  }
                }
              }

              if (hasQtpItem) {
                setQtpConfirmModalInfo({
                  type: 'removeItem',
                  status: hasMandatoryQtp ? 'mandatory' : 'optional',
                });
              } else {
                onRemovePartial();
              }
            }}
            disabled={selectButtonDisabled}
          >
            {t('delete.selected.items')}
          </Button>
          <Button
            type="link"
            className="p-0 h-auto leading-none text-sm font-normal disabled:cursor-not-allowed disabled:bg-transparent disabled:border-none disabled:text-[#A5A5AE] mx-4 sm:mx-0 flex-shrink-0 empty-cart-button"
            onClick={() => {
              const qtpSKUMap = usePriceStore.getState()?.promotionInfo?.qtpSKUMap;
              if (qtpSKUMap) {
                setQtpConfirmModalInfo({
                  type: 'removeAll',
                  status: 'mandatory',
                });
              } else {
                setRemoveAllOpen(true);
              }
            }}
            disabled={emptyButtonDisabled}
          >
            {t('empty.cart')}
          </Button>
        </div>
      </div>
      <div className="flex-1 flex sm:flex-col sm:gap-y-2 gap-x-3 lg:justify-end md:justify-start mt-4 2xl:mt-0">
        <div>
          <div className="flex items-center gap-x-[6px] flex-shrink-0 flex-1 sm:flex-col sm:gap-y-2 sm:items-start">
            <span className="text-base font-medium text-gray-1100 flex-shrink-0">{t('forgot.something')}</span>
            <div className="relative flex-1 sm:w-full">
              <Input
                placeholder={t('enter.catalog.number')}
                className="w-full catalog-number"
                value={catalogNumber}
                onChange={onCatalogChange}
              />
            </div>
          </div>
          {errorText && (
            <span className="text-sm row-start-2 col-start-2 absolute sm:relative text-primary max-w-[600px]">
              {t(errorText, { catalogNumber: catalogNumberRef.current })}
            </span>
          )}
        </div>
        <div className="flex gap-x-2">
          <InputNumber
            className="w-[104px] flex-shrink-0 sm:flex-1"
            textAlign="center"
            minValue="1"
            maxValue="9999"
            value={quantity}
            onChange={onQuantityChange}
          />
          <Button
            size="small"
            className="h-10 box-border w-[89px] flex-shrink-0 sm:flex-1 custom-add"
            onClick={onAdd}
            disabled={addButtonDisabled}
          >
            {t('add')}
          </Button>
        </div>
      </div>
      <Modal
        saveButtonLabel={t('remove')}
        cancelButtonLabel={t('close')}
        className="w-[520px]"
        isOpen={open}
        size="medium"
        title={t('remove.item')}
        handleClose={() => setOpen(false)}
        handleCancel={() => setOpen(false)}
        handleSave={onRemoveProducts}
      >
        <div>
          <p>{t('remove.tips1')}</p>
          <p>{t('remove.tips2')}</p>
        </div>
      </Modal>
      <Modal
        saveButtonLabel={t('remove')}
        cancelButtonLabel={t('close')}
        className="w-[520px]"
        isOpen={removeAllOpen}
        size="medium"
        title={t('remove.item')}
        handleClose={() => setRemoveAllOpen(false)}
        handleCancel={() => setRemoveAllOpen(false)}
        handleSave={onRemoveAll}
      >
        <div>
          <p>{t('remove.all.tips1')}</p>
          <p>{t('remove.tips2')}</p>
        </div>
      </Modal>

      <CartQtpModel
        currentItem={productStore.products.filter((e) => cartStore.selectedCatalogNumbers.includes(e.catalogNumber))}
        type={qtpConfirmModalInfo?.status}
        qtpConfirmModal={!!qtpConfirmModalInfo}
        handleClose={() => setQtpConfirmModalInfo(null)}
        handleSave={() => {
          if (qtpConfirmModalInfo?.type === 'removeItem') {
            onRemoveProducts();
          } else {
            onRemoveAll();
          }
        }}
      />
    </div>
  );
};
